import {
  SET_BID_STAGING_EQUIPMENT,
  SET_BID_STAGING_DEVICES,
  SET_BID_STAGING_POINTS,
  UPDATE_VALIDATION,
  UPDATE_STAGING_EQUIP,
  UPDATE_STAGING_POINTS,
  DELETE_STAGING_EQUIPMENT,
} from './actiontypes';
import {
  getBuildingStagingPointsByTopics,
  deleteStagingEquipment,
  getBuildingStagingEquipment,
  getBuildingStagingDevices,
  getBuildingStagingPoints,
  publishStagingEquipment,
  validateStagingPoints,
  validateStagingEquipment,
  getEquipmentStaging,
  patchStagingPointUpdate,
  patchStagingEquipUpdate,
  postNewStagingEquip,
  unpublish,
} from '../../api/Api';
import { fetchEquipForBuildingActionCreator, removeMultipleEquipmentAction } from './equipment';
import { fetchLocationsForBuildingAction } from './buildings';
import { unique } from '../../utils/collections';

const setBuildingStagingEquipment = (dispatch, time, buildingId, equipment) =>
  dispatch({
    type: SET_BID_STAGING_EQUIPMENT,
    time,
    buildingId,
    equipment,
  });

const setBuildingStagingDevices = (dispatch, time, buildingId, devices) =>
  dispatch({
    type: SET_BID_STAGING_DEVICES,
    time,
    buildingId,
    devices,
  });

const setBuildingStagingPoints = (dispatch, time, buildingId, points) =>
  dispatch({
    type: SET_BID_STAGING_POINTS,
    time,
    buildingId,
    points,
  });

const dispatchStagingEquipmentUpdate = (dispatch, buildingId, equipment) =>
  dispatch({
    type: UPDATE_STAGING_EQUIP,
    buildingId,
    equipment,
  });

const dispatchStagingPointsUpdate = (dispatch, buildingId, points) =>
  dispatch({
    type: UPDATE_STAGING_POINTS,
    buildingId,
    points,
  });

export const getStagingPointsValidationAction =
  ({ buildingId, topics, force = false }) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        staging: {
          validation: { points: bidsPoints },
        },
      },
    } = getState();
    if (!force && bidsPoints[buildingId]) return Promise.resolve();
    return validateStagingPoints(jwt, buildingId, topics).then(({ points }) => {
      dispatch({ type: UPDATE_VALIDATION, buildingId, points });
      return points;
    });
  };

export const getStagingEquipmentValidationAction =
  ({ buildingId, force = false }) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        staging: {
          validation: { equipment: bidsEquipment },
        },
      },
    } = getState();
    if (!force && bidsEquipment[buildingId]) return Promise.resolve();
    return validateStagingEquipment(jwt, buildingId).then(({ equipment }) => {
      dispatch({ type: UPDATE_VALIDATION, buildingId, equipment });
      return equipment;
    });
  };

export const getStagingDevicesAction =
  ({ buildingId, force = false }) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        staging: { devicesLoadTime },
      },
    } = getState();

    // If we have already loaded into redux, don't reload
    if (!force && devicesLoadTime[buildingId]) return Promise.resolve();
    const requestTime = Date.now();
    return getBuildingStagingDevices(jwt, buildingId).then((ds) =>
      setBuildingStagingDevices(dispatch, requestTime, buildingId, ds)
    );
  };

export const getStagingEquipmentAction =
  ({ buildingId, force = false }) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        staging: { equipmentLoadTime },
      },
    } = getState();

    // If we have already loaded into redux, don't reload
    if (!force && equipmentLoadTime[buildingId]) return Promise.resolve();
    const requestTime = Date.now();
    return getBuildingStagingEquipment(jwt, buildingId).then((es) =>
      setBuildingStagingEquipment(dispatch, requestTime, buildingId, es)
    );
  };

export const getStagingPointsAction =
  ({ buildingId, force = false }) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        staging: { pointsLoadTime },
      },
    } = getState();

    // If we have already loaded into redux, don't reload
    if (!force && pointsLoadTime[buildingId]) return Promise.resolve();
    const requestTime = Date.now();
    return getBuildingStagingPoints(jwt, buildingId).then((ps) =>
      setBuildingStagingPoints(dispatch, requestTime, buildingId, ps)
    );
  };

const getStagingPointsByTopicAction = (buildingId, topics) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  if (topics.length === 0) return () => {};
  const chunkSize = 150;
  const promises = [];
  for (let i = 0; i < topics.length; i += chunkSize) {
    const chunk = topics.slice(i, i + chunkSize);
    promises.push(
      getBuildingStagingPointsByTopics(jwt, buildingId, chunk).then((points) =>
        dispatchStagingPointsUpdate(dispatch, buildingId, points)
      )
    );
  }
  return Promise.all(promises);
};

const getEquipmentDetailsAction = (buildingId, equipIds) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  const promises = equipIds.map((equipId) => getEquipmentStaging(jwt, buildingId, equipId));
  return Promise.all(promises)
    .then((results) => results.flat())
    .then((equipment) => dispatchStagingEquipmentUpdate(dispatch, buildingId, equipment));
};

export const publishEquipmentAction = (buildingId, equipmentList) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return publishStagingEquipment(
    jwt,
    buildingId,
    equipmentList.map((e) => e.equipId)
  ).then((data) => {
    const { equipment, points } = data;
    return Promise.all([
      dispatch({ type: UPDATE_VALIDATION, buildingId, equipment, points }),
      dispatch(
        getStagingPointsByTopicAction(
          buildingId,
          unique(equipmentList.flatMap((e) => e.points.map((p) => p.topic)))
        )
      ),
      dispatch(
        getEquipmentDetailsAction(
          buildingId,
          equipmentList.map((e) => e.equipId)
        )
      ),
      dispatch(fetchEquipForBuildingActionCreator(buildingId, true, true, jwt, true)),
    ]).then(() => data);
  });
};

export const updateStagingPoints = (buildingId, pointsUpdates) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return patchStagingPointUpdate(jwt, buildingId, pointsUpdates)
    .then((points) => dispatchStagingPointsUpdate(dispatch, buildingId, points))
    .then((result) => {
      // We do not want the validate to be blocking
      dispatch(
        getStagingPointsValidationAction({
          buildingId,
          force: true,
          topics: result.points?.map(({ topic }) => topic),
        })
      );
      return result;
    });
};

export const createStagingEquipment = (buildingId, name) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return postNewStagingEquip(jwt, buildingId, name)
    .then((equipment) => dispatchStagingEquipmentUpdate(dispatch, buildingId, [equipment]))
    .then((result) => {
      // We do not want the validate to be blocking
      dispatch(getStagingEquipmentValidationAction({ buildingId, force: true }));
      return result;
    });
};

export const updateStagingEquipment = (buildingId, equipUpdates) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return patchStagingEquipUpdate(jwt, buildingId, equipUpdates)
    .then((equipment) => dispatchStagingEquipmentUpdate(dispatch, buildingId, equipment))
    .then((result) => {
      // We do not want the validate to be blocking
      dispatch(getStagingEquipmentValidationAction({ buildingId, force: true }));
      return result;
    });
};

/**
 * Demote points, equipment, and/or point-equipment relationships. This does not do any state refreshes as it's currently expected to be used in conjunction with other operations against the specified points
 */
export const unpublishStagingAction =
  (buildingId, equipmentIds, pointIds, pointEquipmentRelationships) => (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        staging: { equipmentByBuilding, pointsByBuilding },
      },
    } = getState();
    const unpublishedEquipIds = equipmentByBuilding[buildingId]
      .filter((e) => e.publishedEquipmentId && equipmentIds.includes(e.publishedEquipmentId))
      .map((e) => e.equipId);
    const unpublishedTopics = Object.values(pointsByBuilding[buildingId])
      .filter((p) => p.publishedPointId && pointIds.includes(p.publishedPointId))
      .map((p) => p.topic);
    return unpublish(jwt, buildingId, equipmentIds, pointIds, pointEquipmentRelationships).then(
      (result) =>
        Promise.all([
          dispatch(removeMultipleEquipmentAction(buildingId, result.equipment_deleted)),
          // Need to refresh equipment to ensure relationships refresh
          dispatch(fetchEquipForBuildingActionCreator(buildingId, true, true, jwt, true)),
          // Need to refresh points to update demoted points and equipment
          dispatch(getEquipmentDetailsAction(buildingId, unpublishedEquipIds)),
          dispatch(getStagingPointsByTopicAction(buildingId, unpublishedTopics)),
          // Need to refresh locations in case demoted equipment belonged to locations(s)
          dispatch(fetchLocationsForBuildingAction(buildingId, true)),
        ])
    );
  };

/**
 * Delete an empty staging equipment
 */
export const stagingEquipmentDelete = (buildingId, equipIds) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return deleteStagingEquipment(jwt, buildingId, equipIds).then((response) => {
    dispatch({
      type: DELETE_STAGING_EQUIPMENT,
      buildingId,
      equipIds: response.equipment_deleted,
    });
    return equipIds;
  });
};
